import React, { useEffect } from 'react';
import useForm from '~/app/modules/form/useForm';
import { ServiceClosePayload } from '~/types';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import { useCostCategories, useCustomers, useProjects, useWorkOrderHPCodes, useWorkTypes } from '~/app/data/statuses/issue';
import Select from '~/app/modules/form/Select';
import { useStoreActions, useStoreState } from '~/store';
import WorkHoursInput from '~/app/modules/form/WorkHoursInput';
import Text from '~/app/modules/form/Text';
import Textarea from '~/app/modules/form/Textarea';
import Date from '~/app/modules/form/Date';
import moment from 'moment';

export type WorkOrderComponent = React.FC;

const WorkOrder: WorkOrderComponent = () => {
    const { t } = useTranslation('issue');

    const { values: formValues, fieldEvents: formFieldEvents, errors } = useForm<ServiceClosePayload>({ formName: 'closeService' });
    
    const machine = useStoreState(state => state.machine.data);
    const serviceDetails = useStoreState(state => state.service.data);

    const isWorkOrderFormActive = useStoreState(state => state.service.isWorkOrderFormActive);

    const HPCodes = useStoreState(state => state.issue.HPCodes);
    const issueHpCodes = useWorkOrderHPCodes(HPCodes);
    const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
    const hasNextServiceSection = useStoreState(state => state.service.hasNextServiceSection);

    const workTypes = useStoreState(state => state.issue.workTypes);
    const workorderTypes = useWorkTypes(workTypes);

    const costCategories = useStoreState(state => state.issue.costCategories);
    const workorderCostCategories = useCostCategories(costCategories);

    const customers = useStoreState(state => state.issue.customers);
    const workorderCustomers = useCustomers(customers);
    const displayCustomer = useStoreState(state => state.issue.displayCustomer);
    const setDisplayCustomer = useStoreActions(action => action.issue.setDisplayCustomer);

    const projects = useStoreState(state => state.projects.list);
    const workorderProjects = useProjects(projects);
    const displayProject = useStoreState(state => state.issue.displayProject);
    const setDisplayProject = useStoreActions(action => action.issue.setDisplayProject);

    const selectedCategory = costCategories.find(({ id }) => Number(id) === Number(formValues?.costCategory));
    const displayOverTime50 = isWorkOrderFormActive && !!selectedCategory?.costData?.overtime1Percentage;
    const displayOverTime100 = isWorkOrderFormActive && !!selectedCategory?.costData?.overtime2Percentage;

    useEffect(() => {
        const selectedCategoryId = Number(formValues?.costCategory);
        const selectedCategory = costCategories.find(({ id }) => Number(id) === selectedCategoryId);
    
        setDisplayCustomer(isWorkOrderFormActive && !!selectedCategory?.displayCustomer);
    }, [costCategories, formValues?.costCategory, isWorkOrderFormActive, setDisplayCustomer]);

    useEffect(() => {
        const selectedCustomerId = Number(formValues?.customer);
        const selectedCustomer = customers.find(({ id }) => Number(id) === selectedCustomerId);
        setDisplayProject(isWorkOrderFormActive && !!selectedCustomer?.displayProject);
    }, [customers, formValues?.customer, isWorkOrderFormActive, setDisplayCustomer, setDisplayProject]);

    return (
        <div className={classes['workorder-container']}>
            <Textarea
                className={`${classes['input-container']}`}
                placeholder={t('issueClose.comment.placeholder')}
                descriptionPosition="top"
                name="comment"
                defaultValue={formValues.comment}
                errors={errors.comment}
                {...formFieldEvents}
            />
            {!!machine.isOperatingTimeVisible && <Text
                className={`${classes['input-container']}`}
                inputClassName={`${classes['input']}`}
                name="operatingTime"
                type="number"
                placeholder={t('operatingTime.placeholder')}
                autoComplete="off"
                min={0}
                errors={errors.operatingTime}
                {...formFieldEvents}
            />}
            {!!machine.isOperatingCountVisible && <Text
                className={`${classes['input-container']}`}
                inputClassName={`${classes['input']}`}
                name="operatingCount"
                type="number"
                placeholder={t('operatingCount.placeholder')}
                autoComplete="off"
                min={0}
                errors={errors.operatingCount}
                {...formFieldEvents}
            />}
            {hasNextServiceSection === true && (
                <div className={classes['next-service-container']}>
                    <div className={classes['next-service-wrapper']}>
                        <div>{t('nextServicePerformedAt')}</div>
                        {serviceDetails?.primaryMeterUnit !== null &&
                            <Text
                                placeholder={t('nextDynamicServiceOccurrence.placeholder')}
                                name="nextDynamicServiceOccurrence"
                                defaultValue={formValues.nextDynamicServiceOccurrence}
                                type='number'
                                className={classes['next-service']}
                                hasError={errors?.nextDynamicServiceOccurrence !== undefined}
                                errorMessage={t('nextDynamicServiceOccurrence.errorMin', { min: serviceDetails.operatingTime })}
                                errors={errors.nextDynamicServiceOccurrence}
                                {...formFieldEvents}
                            />
                        }
                        {serviceDetails?.scheduleAt !== null &&
                            <Date
                                placeholder={t('nextDynamicScheduleAt.placeholder')}
                                name="nextDynamicScheduleAt"
                                defaultValue={formValues.nextDynamicScheduleAt}
                                className={classes['next-service']}
                                format={'DD.MM.YYYY'} type='date'
                                min={moment().format('YYYY-MM-DD')}
                                errors={errors.nextDynamicScheduleAt}
                                {...formFieldEvents}
                            />
                        }
                    </div>
                    <div className={classes['next-service-wrapper']}>
                        <div>{t('noticeBeforeTheNextService')}</div>
                        {serviceDetails?.primaryMeterUnit !== null &&
                            <Text
                                placeholder={t('nextAlertServiceOccurrence.placeholder')}
                                name="nextAlertServiceOccurrence"
                                defaultValue={formValues.nextAlertServiceOccurrence}
                                type='number'
                                className={classes['next-service']}
                                hasError={errors?.nextAlertServiceOccurrence !== undefined}
                                errorMessage={t('nextAlertServiceOccurrence.errorBetween', { min: 1, max: (Number(serviceDetails.primaryMeterUnit) - 1) })}
                                errors={errors.nextAlertServiceOccurrence}
                                {...formFieldEvents}
                            />
                        }
                        {serviceDetails?.scheduleAt !== null &&
                            <Text
                                placeholder={t('nextAlertScheduleDays.placeholder')}
                                name="nextAlertScheduleDays"
                                defaultValue={formValues.nextAlertScheduleDays}
                                type='number'
                                className={classes['next-service']}
                                hasError={errors?.nextAlertScheduleDays !== undefined}
                                errorMessage={t('nextAlertScheduleDays.errorMin', { min: 1 })}
                                errors={errors.nextAlertScheduleDays}
                                {...formFieldEvents}
                            />
                        }
                    </div>
                </div>
            )}
            {isHPCodeRequired && <Select
                className={`${classes['input-container']}`}
                name="hpCode"
                placeholder={t('hpCode.placeholder')}
                options={issueHpCodes}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.hpCode}
                showEmptyOption={true}
                emptyOptionValue={0}
                errors={errors.hpCode}
            />}
            <Select
                className={`${classes['input-container']}`}
                name="workType"
                placeholder={t('workType.placeholder')}
                options={workorderTypes}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.workType}
                showEmptyOption={true}
                emptyOptionValue={0}
                errors={errors.workType}
            />
            <Select
                className={`${classes['input-container']}`}
                name="costCategory"
                placeholder={t('costCategory.placeholder')}
                options={workorderCostCategories}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.costCategory}
                showEmptyOption={true}
                emptyOptionValue={0}
                errors={errors.costCategory}
            />
            {displayCustomer && <Select
                className={`${classes['input-container']}`}
                name="customer"
                placeholder={t('customer.placeholder')}
                options={workorderCustomers}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.customer}
                showEmptyOption={true}
                emptyOptionValue={0}
                errors={errors.customer}
            />}
            {displayProject && <Select
                className={`${classes['input-container']}`}
                name="project"
                placeholder={t('project.placeholder')}
                options={workorderProjects}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.project}
                showEmptyOption={true}
                emptyOptionValue={0}
                errors={errors.project}
            />}
            <WorkHoursInput
                placeholder={t('ordinaryHours.placeholder')}
                name="ordinaryHours"
                defaultValue={formValues.ordinaryHours}
                errors={errors.ordinaryHours}
                {...formFieldEvents}
            />
            {displayOverTime50 && <WorkHoursInput
                placeholder={t('overTime50.placeholder', { overTime50: `${selectedCategory?.costData?.overtime1Percentage}%` })}
                name="overTime50"
                defaultValue={formValues.overTime50}
                errors={errors.overTime50}
                {...formFieldEvents}
            />}
            {displayOverTime100 && <WorkHoursInput
                placeholder={t('overTime100.placeholder', { overTime100: `${selectedCategory?.costData?.overtime2Percentage}%` })}
                name="overTime100"
                defaultValue={formValues.overTime100}
                errors={errors.overTime100}
                {...formFieldEvents}
            />}
        </div>
    );
};

export default WorkOrder;